<template>
  <tile class="opc-pbx">
    <foldable v-model="isExpanded" :title="$t('billing.planform.title.zoomphone')">
      <template #head>
      <div>
        <div class="opc-pbx-head">
          <div class="opc-pbx-head__logo--left">
            <img class="opc-pbx__logo-icon" :src="icon" :alt="$t('billing.planform.title.zoomphone')" />
          </div>
          <div class="opc-pbx-head__logo">
            <div class="opc-pbx__logo opc-pbx__title-wrapper">
              {{ $t('billing.pbx.title') }}
            </div>
            <div class="opc-config__collapse" v-if="!isExpanded">
              <div v-for="selectedPlansLabel in selectedPlansLabelList" :key="selectedPlansLabel">
                {{ selectedPlansLabel }}
              </div>
            </div>
          </div>
        </div>
      </div>
      </template>
      <div class="opc-pbx__main">
        <zm-banner show-close v-if="mx_alertOpts" ref="mx_alert" v-bind="mx_alertOpts" />
        <h2 id="label-title-1" class="opc-pbx__title">
          {{ $t('billing.pbx.buy_more_calling_plans') }}
        </h2>
        <p class="opc-pbx__subtitle">
          <span v-if="boughtCallingSum > 0">{{ $t('billing.pbx.current_phone_licenses', [boughtCallingSum]) }} </span>{{ $t('billing.pbx.get_more_licenses') }}
        </p>
        <div>
          <config-section :show-head="regionsConfig[activeRegionKey].callings.length"
            :show-action="!isDisableAdd2Cart && !testRegionalCallingPlansFull(pbxBaseInfo.billingCycle)"
            style="z-index: 1">
            <template #title>
              <span>{{ $t('billing.pbx.calling_plans') }}</span>
            </template>
            <template #description>
              <span>{{ callingPlansDesc }}</span>
            </template>
            <number-select ref="callingSelect" :data-tracking="dataTrackingCallingPlan"
              :optionList="mapSelectOptions(regionalCallingPlans, pbxBaseInfo.billingCycle)"
              :deletable="callingPlanDeletable" :before-change="beforeChangeCallingPlan"
              :upsell-mode="true"
              :key="pbxBaseInfo.billingCycle"
              :title="$t('billing.pbx.calling_plans')" v-model="regionsConfig[activeRegionKey].callings" />
            <template #action>
              <zm-button data-testid="checkout-button-235" type="link" @click="addAnotherCallingPlan">
                {{ $t('billing.pbx.add_another_calling_plan') }}
              </zm-button>
            </template>
          </config-section>
          <!-- banner -->
          <zm-banner class="reason-tips" v-if="licenseTips" type="info" :show-close="false">
              <p v-html="licenseTips"></p>
          </zm-banner>
          <!-- The method of the component instance will be called through ref(pbxCardRef) to facilitate the reuse of code logic -->
          <z-p-base-part :activeRegionKey="activeRegionKey" :planBillInfo="planBillInfo" @cycle-change="handleCycleChange" />
        </div>
      </div>
    </foldable>
  </tile>
</template>

<script>
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import ConfigSection from '../component/config-section.vue';
import NumberSelect from '../component/select-number-list.vue';
import icon from '@/assets/plans/logo_pbx.svg';
import mxEdit from './mx-edit';
import { mapGetters, mapMutations } from 'vuex';
import { computedPbxBasePlans, computedCallingPlans, computedPbxPlans, computedPbxCallingPlans } from '@/common/service/computers/plans/pbx';
import { i18n_cycle } from '@/utils/i18n-shortcuts';
import {
  trackingAddAnotherCallingPlan,
  getZPCallingFeesText
} from '@/plans/ZOOM_PHONE/helper/utils';
import { BILLING_CYCLE, planTypes } from '@/common/constants';
import ZPBasePart from '../component/zp-base-part.vue';
import { PBX_REGIONS } from '../helper/constants';
import deskPhoneErrorAlertMixin from '@/plans/mx/desk-phone-error-alert-mixin';
import merge from 'deepmerge'

export default {
  mixins: [mxEdit, deskPhoneErrorAlertMixin],
  components: {
    Foldable,
    Tile,
    ConfigSection,
    NumberSelect,
    ZPBasePart,
  },
  data() {
    return {
      icon,
      isExpanded: true,
    };
  },
  computed: {
    ...mapGetters(['boughtPlans', 'isDisableAdd2Cart', 'pbxBaseInfo']),
    selectedPbxPlans() {
      return computedPbxPlans(this.getSelectedPlans);
    },
    selectedPbxBasePlan() {
      return computedPbxBasePlans(this.selectedPbxPlans)?.[0];
    },
    selectedCallingPlan() {
      return computedPbxCallingPlans(this.selectedPbxPlans)?.[0];
    },
    planBillInfo() {
      const plan = computedCallingPlans(this.boughtPlans)?.[0] ?? {};
      const basePlan = computedPbxBasePlans(this.boughtPlans)?.[0] ?? {};
      const payment = getZPCallingFeesText(basePlan).title;
      const billingCycle = basePlan.billing_cycle === BILLING_CYCLE.PAY_AS_YOU_GO ? BILLING_CYCLE.MONTHLY : plan.billing_cycle;
      return {
        billing_cycle: billingCycle,
        payment
      }
    },
    callingPlanDeletable() {
      // Check if the calling plan is deletable
      let count = 0
      Object.keys(this.regionsConfig).forEach((key) => {
        // check if the calling plan is disabled
        const callings = this.regionsConfig[key].callings.filter(p => !this.plancodeMapSubscriptionDisabled[p.id]);
        count += callings.length
      })
      return count > 1;
    },
    licenseTips() {
      const hasSameQuantity = this.selectedPbxPlans.some((plan) => {
        const bpPlan = this.boughtPlans.find((bp) => bp.plan_code === plan.plan_code && plan.isStandardPrice === false);
        return bpPlan?.cartInfo.selected_number === plan.cartInfo.selected_number;
      });
      if (hasSameQuantity) {
        return this.$t('billing.planform.meeting_license_custom_pricing');
      }
      return '';
    },
    boughtCallingSum() {
      const boughtCallingPlans = computedPbxCallingPlans(this.boughtPlans.filter(p => p.type === planTypes.ZOOM_PHONE));
      return boughtCallingPlans.reduce((sum, plan) => sum + plan.cartInfo.selected_number, 0);
    }
  },
  methods: {
    ...mapMutations({
      setPbxBasePlanCode: 'setPbxBasePlanCode',
      setPbxBillingCycle: 'setPbxBillingCycle'
    }),
    i18n_cycle,
    addAnotherCallingPlan() {
      this.$refs.callingSelect.newItem();
      trackingAddAnotherCallingPlan(this.getRegionLabelById(this.activeRegionKey));
    },
    handleCycleChange() {
      const overwriteMerge = (_, sourceArray) => sourceArray
      this.regionsConfig = merge(this.regionsConfig,this.fillSelectModel(),{ arrayMerge: overwriteMerge })
    },
    async initPbxForm() {
      if(this.selectedPbxBasePlan) {
        this.setPbxBasePlanCode(this.selectedPbxBasePlan.plan_code);
      }
      if(this.selectedCallingPlan) {
        this.setPbxBillingCycle(this.selectedCallingPlan.billing_cycle);
      } else if(this.selectedPbxBasePlan && !this.selectedPbxBasePlan.is_payg) {
        this.setPbxBillingCycle(this.selectedPbxBasePlan.billing_cycle);
      } else {
        this.setPbxBillingCycle(BILLING_CYCLE.ANNUAL);
      }

      this.region = PBX_REGIONS.US_AND_CANADA
      this.activeRegionKey = this.region
      this.fillSelectModel(this.regionsConfig);
    }
  },
  created() {
    this.initPbxForm();
  }
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-pbx {
  &__logo {
    display: flex;
    align-items: center;
    margin-bottom: 2px;

    &-icon {
      display: block;
      width: auto;
      height: 100%;
    }
  }

  &__title {
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 28px;
  }
  &__subtitle {
    margin-bottom: 16px;
  }

  &__regions {
    margin: 16px 0;
  }
}
</style>
